import React, { useState } from 'react';
import { makeStyles, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { ReferenceType } from '@modules/client';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { engineRevampMonospaceTextStyle } from '@modules/ui/shared/utils/styles';
import { getReferencePageUrl } from '../../utils/apiReferencePageNavUtils';
import ReferenceSummarySectionTitle from './ReferenceSummarySectionTitle';

const useReferenceInheritedByStyles = makeStyles()((theme) => ({
  container: {
    marginTop: '24px',
  },
  descendantsContainer: {
    ...engineRevampMonospaceTextStyle,
    color: theme.palette.content.standard,
  },
  moreLink: {
    ...engineRevampMonospaceTextStyle,
    color: theme.palette.content.standard,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

interface ReferenceInheritedByProps {
  referenceType: ReferenceType;
  descendants: string[];
}

export default function ReferenceInheritedBy({
  referenceType,
  descendants,
}: ReferenceInheritedByProps) {
  const { translate } = useTranslation();
  const { classes } = useReferenceInheritedByStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!descendants || descendants.length === 0) return null;

  const maxVisible = 5;
  const visibleDescendants = isExpanded ? descendants : descendants.slice(0, maxVisible);
  const remainingCount = descendants.length - maxVisible;

  return (
    <div className={classes.container}>
      <ReferenceSummarySectionTitle title={translate('Label.InheritedBy') || 'Inherited by'} />
      <Typography variant='body2' component='div' className={classes.descendantsContainer}>
        {visibleDescendants.map((descendant, index) => (
          <React.Fragment key={descendant}>
            <DynamicallyRouted.Link
              href={getReferencePageUrl(referenceType, descendant)}
              color='inherit'>
              {descendant}
            </DynamicallyRouted.Link>
            {index < visibleDescendants.length - 1 && ', '}
          </React.Fragment>
        ))}
        {!isExpanded && remainingCount > 0 && (
          <React.Fragment>
            {', '}
            <button type='button' className={classes.moreLink} onClick={() => setIsExpanded(true)}>
              {translate('Label.ShowMore') || 'Show more...'}
            </button>
          </React.Fragment>
        )}
        {isExpanded && remainingCount > 0 && (
          <React.Fragment>
            {', '}
            <button type='button' className={classes.moreLink} onClick={() => setIsExpanded(false)}>
              {translate('Label.ShowLess') || 'Show less...'}
            </button>
          </React.Fragment>
        )}
      </Typography>
    </div>
  );
}
